import { filter, pipe } from "callbag-basics";
import subscribe from "callbag-subscribe";
import { ChangeEvent, FC, useCallback, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Redirect, useHistory, useParams } from "react-router";

import { ErrorBoundary } from "components/error-boundary";
import { FixedSaveBar, SaveBarMode } from "components/form";
import { BackButton, Grid, ViewStack } from "components/grid";
import Role from "constants/role";
import { EventPlace, trackEngagement } from "helpers/analytics";
import { useAgentFlag } from "hooks/use-agent-flag";
import { SettingsRoutes } from "routes/settings/constants";
import { ContentWrapper, PageWrapper } from "routes/settings/styled";
import { actions$ } from "store/action-stream";
import { CRUDAction, IEntityCreateSuccess, RequestAction } from "store/entities/actions";
import { selectLoggedInAgentPermission } from "store/entities/agents";
import { ContactFormActionNames, ContactFormActions } from "store/entities/contact-forms/actions";
import { IContactForm } from "store/entities/contact-forms/interfaces";
import { selectContactForms } from "store/entities/contact-forms/selectors";
import { selectDefaultTeamId, selectLicense } from "store/entities/license";
import { IActionWithPayload } from "store/helper";

import SettingsViewContainer from "../../ViewContainer";
import { ConfirmContactFormChangesModal } from "../ConfirmContactFormChangesModal";
import { ValidationResult } from "../validation";

import { ContactForm } from "./ContactForm";

export const EditContactForm: FC = () => {
  const dispatch = useDispatch();
  const params = useParams<{ id: string }>();
  const history = useHistory();

  const backButtonRef = useRef(null);

  const defaultTeamID = useSelector(selectDefaultTeamId);
  const license = useSelector(selectLicense);
  const contactForms = useSelector(selectContactForms);
  const isAdmin = useSelector(selectLoggedInAgentPermission) === Role.Owner;

  const [confirmChangesDisabled, toggleConfirmChangesDisabled] = useAgentFlag("confirmContactFormChangesDisabled");

  const [isContactFormChanged, toggleContactFormChanged] = useState(false);
  const [isSaving, toggleIsSaving] = useState(false);
  const [livechatGroupChanged, toggleLivechatGroupChanged] = useState(false);
  const [saveModalShown, toggleSaveModalShown] = useState(false);
  const [formValidations, setFormValidations] = useState<{ [Key in keyof IContactForm]?: [boolean, ValidationResult] }>(
    { name: [false, { type: "ok" }] }
  );
  const isDefault = params.id === "default";
  const contactFormBase = isDefault
    ? { id: "default", name: "Default form for widget", teamID: defaultTeamID, ...license.contactFormDefaults }
    : contactForms[params.id];
  const oldConsentMessage = contactFormBase.consentMessage;

  const shouldUpdatePreview = Object.entries(formValidations).every(
    ([key, value]) => key === "name" || value[1].type === "ok"
  );
  const isFormValid = shouldUpdatePreview && formValidations.name?.[1]?.type === "ok";

  const saveBarMode = isSaving ? SaveBarMode.Saving : isContactFormChanged ? SaveBarMode.OfferSave : SaveBarMode.Hidden;

  const [contactForm, setContactForm] = useState<Partial<IContactForm>>(contactFormBase);

  const handleBack = useCallback(() => {
    trackEngagement("Settings: Contact forms editing back button pressed", EventPlace.Settings);
    history.push(SettingsRoutes.ContactForms);
  }, [history]);

  const handleChange = useCallback(
    (fieldName: keyof IContactForm) =>
      (event: ChangeEvent<HTMLInputElement>): void => {
        toggleContactFormChanged(true);
        setContactForm({ ...contactForm, [fieldName]: event.target.value });
      },
    [contactForm]
  );

  const handleConsentChange = useCallback(
    (item: IContactForm["consentMode"]) => {
      toggleContactFormChanged(true);
      setContactForm({
        ...contactForm,
        consentMode: item,
        ...(item === "disabled" ? { consentMessage: oldConsentMessage } : {}),
      });
      if (item === "disabled") {
        setFormValidations({ ...formValidations, consentMessage: [false, { type: "ok" }] });
      }
    },
    [contactForm, formValidations, oldConsentMessage]
  );
  const handleTeamChange = useCallback(
    (teamID: string) => {
      toggleContactFormChanged(true);
      setContactForm({ ...contactForm, teamID });
    },
    [contactForm]
  );
  const handleSubmit = useCallback(() => {
    toggleIsSaving(true);
    trackEngagement("Settings: Contact form updated", EventPlace.Settings);

    if (livechatGroupChanged) {
      trackEngagement("Settings: Contact form LiveChat groups assigned while adding", EventPlace.Settings, {
        livechatGroupCount: (contactForm.livechatGroupIDs || []).length,
      });
    }

    if (contactForm.id === "default") {
      dispatch(ContactFormActions.updateDefault({ value: contactForm }));
    } else {
      dispatch(ContactFormActions.update({ value: contactForm }));
    }
  }, [contactForm, dispatch, livechatGroupChanged]);

  const handleLivechatGroupChange = useCallback(
    (groupIDs: string[]) => {
      toggleContactFormChanged(true);
      toggleLivechatGroupChanged(true);
      setContactForm({
        ...contactForm,
        livechatGroupIDs: groupIDs,
      });
      setFormValidations({
        ...formValidations,
        livechatGroupIDs: [
          true,
          {
            type: groupIDs.length > 0 ? "ok" : "required",
          },
        ],
      });
    },
    [contactForm]
  );

  const handleSave = useCallback(() => {
    if (confirmChangesDisabled) {
      handleSubmit();
    } else {
      toggleSaveModalShown(true);
    }
  }, [confirmChangesDisabled, handleSubmit]);

  const handleSaveModalClose = useCallback(() => {
    toggleSaveModalShown(false);
  }, []);

  useEffect(() => {
    const actionTypeFamily = ContactFormActionNames[CRUDAction.UPDATE];
    const defaultActionTypeFamily = ContactFormActionNames.UPDATE_DEFAULT;

    const unsubscribe = pipe(
      actions$,
      filter((action: IActionWithPayload<string, IEntityCreateSuccess<IContactForm>>) =>
        [
          actionTypeFamily[RequestAction.SUCCESS],
          actionTypeFamily[RequestAction.FAILURE],
          defaultActionTypeFamily[RequestAction.SUCCESS],
          defaultActionTypeFamily[RequestAction.FAILURE],
        ].includes(action.type)
      ),
      subscribe((action: IActionWithPayload<string, IEntityCreateSuccess<IContactForm>>) => {
        if (action.type === actionTypeFamily[RequestAction.FAILURE]) {
          toggleIsSaving(false);
        } else {
          history.push(SettingsRoutes.ContactForms);
        }
      })
    );

    return unsubscribe;
  }, [history]);

  const handleValidation = useCallback(
    (fieldName: keyof Partial<IContactForm>, validationResult: ValidationResult) => {
      const [dirtyState] = formValidations[fieldName] || [false];

      setFormValidations({ ...formValidations, [fieldName]: [dirtyState, validationResult] });
    },
    [formValidations]
  );

  const handleDirty = useCallback(
    (fieldName: keyof Partial<IContactForm>, dirty: boolean) => {
      const [, validationResult] = formValidations[fieldName] || [false, { type: "ok" }];

      setFormValidations({ ...formValidations, [fieldName]: [dirty, validationResult] });
    },
    [formValidations]
  );

  return (
    <ErrorBoundary onError={() => history.push(SettingsRoutes.ContactForms)}>
      {contactForm || !isAdmin ? (
        <>
          {saveModalShown && (
            <ConfirmContactFormChangesModal
              onClose={handleSaveModalClose}
              onSubmit={handleSubmit}
              onConfirmationDisableToggle={toggleConfirmChangesDisabled}
            />
          )}
          <Grid relativeWidths={[4, 1]} minWidths={[0, 320]} focusedColumn={0} overflowHidden>
            <ViewStack>
              <SettingsViewContainer
                noBackButton
                title="Edit contact form"
                leftNodes={
                  <BackButton ref={backButtonRef} onClick={handleBack}>
                    Back
                  </BackButton>
                }
              >
                <PageWrapper>
                  <ContentWrapper>
                    <ContactForm
                      data={contactForm}
                      onChange={handleChange}
                      onConsentChange={handleConsentChange}
                      onTeamChange={handleTeamChange}
                      onValidation={handleValidation}
                      onLivechatGroupChange={handleLivechatGroupChange}
                      onDirty={handleDirty}
                      validations={formValidations}
                      backButtonRef={backButtonRef}
                      isDefault={isDefault}
                    />
                  </ContentWrapper>
                </PageWrapper>
              </SettingsViewContainer>
            </ViewStack>
          </Grid>
          <FixedSaveBar
            mode={saveBarMode}
            onSave={handleSave}
            customSaveLabel="Save changes"
            onCancel={handleBack}
            saveDisabled={!isContactFormChanged || !isFormValid}
          />
        </>
      ) : (
        <Redirect to={SettingsRoutes.ContactForms} />
      )}
    </ErrorBoundary>
  );
};
